@if (loading && !data) {
  <div class="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-600 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400">
    Loading accommodation…
  </div>
}

@if (data) {
  @if (showEmptyTabState) {
    <div class="rounded-2xl border border-gray-200 bg-white p-8 text-center dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Accommodation</h3>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        @if (!data.venue_has_rooms) {
          This venue does not have configured accommodation rooms.
        } @else {
          This event has no accommodation product flagged to trigger the tab.
        }
      </p>
    </div>
  } @else {

    <!-- Header / breakfast count (TVG-61) -->
    <div class="mb-6 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Accommodation</h3>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {{ event.venue?.name }} — {{ data.rooms.length }} rooms
        </p>
      </div>
      <div class="flex items-center gap-4">
        <div class="text-right">
          <div class="text-2xl font-semibold text-gray-900 dark:text-white">
            {{ data.breakfast_count }}
          </div>
          <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Breakfasts</div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">

      <!-- Rooms grid (TVG-58) -->
      <div class="lg:col-span-2 space-y-4">
        @for (room of data.rooms; track room.number) {
          <div class="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="flex items-start justify-between gap-4">
              <div>
                <div class="flex items-baseline gap-2">
                  <h4 class="text-base font-semibold text-gray-900 dark:text-white">
                    Room {{ room.number }}
                  </h4>
                  <span class="text-sm text-gray-600 dark:text-gray-400">{{ room.name }}</span>
                </div>
                <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  @if (room.floor) {
                    {{ room.floor }} ·
                  }
                  {{ room.bed_configuration }} · {{ room.en_suite }} · max {{ room.max_occupancy }}
                </div>
                @if (room.notes) {
                  <div class="mt-1 text-xs italic text-gray-500 dark:text-gray-400">{{ room.notes }}</div>
                }
              </div>
              <div class="text-right text-xs text-gray-500 dark:text-gray-400">
                Occupancy<br>
                <span class="text-base font-semibold text-gray-900 dark:text-white">
                  {{ room.allocated_count }}
                </span>
              </div>
            </div>

            <div class="mt-4 space-y-2">
              @for (guest of room.allocated_guests; track guest.id) {
                <button type="button"
                        class="w-full rounded-lg border border-gray-200 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/40 dark:hover:bg-gray-800"
                        (click)="openEdit(room, guest)">
                  <div class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ guest.full_name }}
                  </div>
                  <div class="text-xs text-gray-600 dark:text-gray-400">
                    {{ guestContact(guest) || '—' }}
                  </div>
                  @if (guest.accommodation_role) {
                    <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Role: {{ guest.accommodation_role }}
                    </div>
                  }
                </button>
              }

              @if (canManage) {
                <button type="button"
                        class="w-full rounded-lg border border-dashed border-gray-300 p-3 text-center text-sm text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-gray-200"
                        (click)="openAddForRoom(room)">
                  + Allocate guest to this room
                </button>
              }
            </div>
          </div>
        }
      </div>

      <!-- Unallocated guests panel (TVG-59) -->
      <div class="space-y-3 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
        <div>
          <h4 class="text-base font-semibold text-gray-900 dark:text-white">Unallocated Guests</h4>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Guests flagged for accommodation but not yet placed in a room.
          </p>
        </div>

        @if (data.unallocated_guests.length === 0) {
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400">
            All flagged guests are allocated.
          </div>
        } @else {
          @for (guest of data.unallocated_guests; track guest.id) {
            <button type="button"
                    class="w-full rounded-lg border border-gray-200 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/40 dark:hover:bg-gray-800"
                    (click)="openFromUnallocated(guest)"
                    [disabled]="!canManage">
              <div class="text-sm font-medium text-gray-900 dark:text-white">
                {{ guest.full_name }}
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400">
                {{ guestContact(guest) || '—' }}
              </div>
            </button>
          }
        }
      </div>
    </div>

  }
}

<!-- Allocate/edit modal (TVG-60) -->
@if (modalContext) {
  <app-accommodation-modal
    [show]="modalOpen"
    [context]="modalContext"
    [eventId]="event.id"
    [customerPortal]="customerPortal"
    (modalClose)="onModalClose()"
    (allocationSaved)="onAllocationSaved()">
  </app-accommodation-modal>
}
